<template>
  <div
    id="view-container"
    class="view-container"
    :style="setContainerBackgroundColors"
  >
    <div
      v-for="slot in homepageSlotsInOrder"
      :key="slot.rowKey"
      :class="[setContainerClass(slot.position), { 'accent-styling': accentStyling }]"
      :style="setComponentBackgroundColor(slot.position)"
      class="homepage-component"
      data-testid="homepage-component"
    >
      <!-- <client-only> -->
      <component
        :is="slot.resolvedComponent"
        v-if="slot.name"
        :component-idx="slot.position"
        :container-idx="containerIdx"
        :container-id="container.id"
        :class="{ 'sticky-container': hasColumnSticky }"
        :dots-rectangle="dotsRectangleOptions"
      />
      <!-- </client-only> -->
    </div>
    <DecorativeBackground
      v-if="showDecorativeBackground && !hasDotsRectangle"
      :version="decorativeBackgroundType"
      :fill-color="decorativeBackgroundColor"
      :flip-horizontal="isLeftOneThird"
      :class="isLightOrPurple && 'light-or-purple'"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import get from 'lodash/get';
import componentEnums from './enums/component-enums';
import containerEnums from './enums/container-enums';
import decorativeBackgroundEnums from './enums/decorative-background-enums';
import LiveFeed from './live_feed/ViewLiveFeed.vue';
import News from './news/ViewNews.vue';
import Events from './events/ViewEvents.vue';
import Programs from './custom/programs/ViewPrograms.vue';
import OurSchools from './custom/our_schools/ViewOurSchools.vue';
import ButtonBar from './custom/buttons/ViewButtons.vue';
import Stats from './custom/stats/ViewStats.vue';
import Spotlight from './custom/spotlights/ViewSpotlights.vue';
import Athletics from './athletics/ViewAthletics.vue';
import CustomCode from './custom/custom_code/ViewCustomCode.vue';
import Parallax from './custom/parallax/ViewParallax.vue';
import About from './custom/about/ViewAbout.vue';
import Video from './custom/video/ViewVideo.vue';
import Timeline from './custom/timeline/ViewTimeline.vue';
import MediaCustomCode from './custom/media_custom_code/ViewMediaCode.vue';
import CustomComponent from './custom/custom_component/ViewCustomComponent.vue';
import CallToAction from './custom/call_to_action/ViewCallToAction.vue';
import DecorativeBackground from './theme_accents/DecorativeBackground.vue';
import { useScreenWidth } from '../../composables/useScreenWidth.js';

const homepageComponentsByName = {
  LiveFeed,
  News,
  Events,
  Programs,
  OurSchools,
  Spotlight,
  Stats,
  Athletics,
  ButtonBar,
  CustomCode,
  Parallax,
  About,
  Video,
  Timeline,
  MediaCustomCode,
  CustomComponent,
  CallToAction,
};

/**
 * Resolves CMS homepage component `name` (e.g. "Live Feed") to a concrete component for `<component :is>`.
 * @param {string | undefined} name
 * @returns {import('vue').Component | string | null}
 */
function resolveHomepageComponent(name) {
  if (!name) return null;
  const key = name.replace(/ /g, '');
  return homepageComponentsByName[key] ?? key;
}

const props = defineProps({
  container: {
    type: Object,
    default: () => {},
  },
  containerIdx: {
    type: Number,
    default: null,
  },
  isLastContainer: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { screenSize } = useScreenWidth();

const accentStyling = computed(() => store.state.accentStyling);

const layout = computed(() => props.container.type);

const hasCustomCode = computed(() =>
  props.container.components.some((component) =>
    [componentEnums.CUSTOM_CODE, componentEnums.MEDIA_CUSTOM_CODE].includes(component.name),
  ),
);

const hasNews = computed(() =>
  props.container.components.some((component) => component.name.includes(componentEnums.NEWS)),
);

const decorativeBackgroundType = computed(() => get(props.container, 'decorativeBackground.type', 'None'));

const decorativeBackgroundColor = computed(() => get(props.container, 'decorativeBackground.color.hex', '#000'));

const decorativeBackgroundNone = computed(() => decorativeBackgroundType.value === decorativeBackgroundEnums.NONE);

const isLeftOneThird = computed(() => layout.value === containerEnums.LEFT_SIDEBAR);

const isRightOneThird = computed(() => layout.value === containerEnums.RIGHT_SIDEBAR);

const hasDotsRectangle = computed(() => decorativeBackgroundType.value === decorativeBackgroundEnums.DOTS_RECTANGLE);

const showDecorativeBackground = computed(
  () =>
    (isLeftOneThird.value || isRightOneThird.value) &&
    !decorativeBackgroundNone.value &&
    accentStyling.value &&
    !props.isLastContainer &&
    !hasCustomCode.value,
);

const showDotsRectangle = computed(() => showDecorativeBackground.value && hasDotsRectangle.value && hasNews.value);

const hasColumnSticky = computed(() => {
  if (hasCustomCode.value) return false;

  return layout.value === containerEnums.ONE_THIRD || showDotsRectangle.value;
});

const dotsRectangleOptions = computed(() => ({
  show: showDotsRectangle.value,
  color: decorativeBackgroundColor.value,
  flip: isLeftOneThird.value,
}));

const bgColors = computed(() =>
  props.container.components.map((component) => get(component.options, 'bg_color.hex', '#fff')),
);

const bgRatio = computed(() => {
  if (layout.value === containerEnums.HALF_SIDEBAR) {
    return '50%';
  }
  if (isRightOneThird.value) {
    return 'calc((100% / 3) * 2)';
  }
  return 'calc(100% / 3)';
});

const setContainerBackgroundColors = computed(() => {
  if (layout.value === containerEnums.ONE_THIRD) {
    return null;
  }

  return {
    '--bg-ratio': bgRatio.value,
    '--first-color': bgColors.value[0],
    '--second-color': bgColors.value[1] || bgColors.value[0],
  };
});

const isLightOrPurple = computed(() => {
  const styles = props.container.components.map((component) => get(component.options, 'style', 'none'));
  return styles.some((style) => ['purple', 'light'].includes(style));
});

const isMobile = computed(() => screenSize.value <= 1280);

const isThreeColumnLayout = computed(() => layout.value === containerEnums.ONE_THIRD);

const isFullLayout = computed(() => layout.value === containerEnums.FULL);

const sortOrder = computed(() => {
  let order = [0, 1];
  if (isThreeColumnLayout.value && isMobile.value) {
    order = [0, 2, 1];
  } else if (isThreeColumnLayout.value && !isMobile.value) {
    order = [0, 1, 2];
  } else if (isFullLayout.value) {
    order = [0];
  }
  return order;
});

/** Homepage slots in `sortOrder`, with `:is` resolved once per row. */
const homepageSlotsInOrder = computed(() => {
  const components = props.container.components ?? [];
  return sortOrder.value.map((position) => {
    const componentConfig = components[position];
    const name = componentConfig?.name;
    const id = componentConfig?.id;
    return {
      position,
      name,
      rowKey: id != null ? id : `slot-${position}`,
      resolvedComponent: resolveHomepageComponent(name),
    };
  });
});

function setComponentBackgroundColor(idx) {
  const { options } = props.container.components[idx];
  return { '--component-bg-color': get(options, 'bg_color.hex', '#fff') };
}

function setContainerClass(idx) {
  const containerClass = ['first', 'fullwidth'];
  if (idx === 1) {
    containerClass[0] = 'last';
  }
  switch (layout.value) {
    case containerEnums.LEFT_SIDEBAR:
      containerClass[1] = 'left-sidebar';
      break;
    case containerEnums.RIGHT_SIDEBAR:
      containerClass[1] = 'right-sidebar';
      break;
    case containerEnums.HALF_SIDEBAR:
      containerClass[1] = 'half-sidebar';
      break;
    case containerEnums.ONE_THIRD:
      containerClass[1] = 'one-third';
      if (idx === 1) {
        containerClass[0] = 'middle';
      } else if (idx === 2) {
        containerClass[0] = 'last';
      }
      break;
    default:
      break;
  }
  return containerClass;
}
</script>

<style scoped lang="scss">
$one-third: calc(100% / 3);

.view-container {
  display: flex;
  flex-flow: row wrap;
  position: relative;
  background: linear-gradient(to right, var(--first-color) var(--bg-ratio), var(--second-color) var(--bg-ratio));
  .homepage-component {
    z-index: 1;
    @media (max-width: 959px) {
      background-color: var(--component-bg-color);
    }
  }
  .active {
    border: 4px solid #009ddb;
  }
  .first.fullwidth {
    width: 100%;
  }
  .first.left-sidebar {
    width: $one-third;
    @media (max-width: 1200px) {
      width: $one-third;
    }
    @media (max-width: 959px) {
      width: 100%;
    }
  }
  .last.left-sidebar {
    flex: 1;
  }
  .first.right-sidebar {
    flex: 1;
  }
  .last.right-sidebar {
    width: $one-third;
    @media (max-width: 1200px) {
      width: $one-third;
    }
    @media (max-width: 959px) {
      width: 100%;
    }
  }
  .half-sidebar {
    width: 50%;
    @media (max-width: 959px) {
      width: 100%;
    }
  }
  .one-third {
    width: $one-third;
    background-color: var(--component-bg-color);
    @media (min-width: 960px) and (max-width: 1280px) {
      width: 50%;
      &.middle {
        width: 100%;
      }
    }
    @media (max-width: 959px) {
      width: 100%;
    }
  }
  .sticky-container {
    position: sticky;
    top: 0;
    height: auto;
  }
}
</style>
